import React, { PropsWithChildren, useEffect, useMemo, useRef } from "react";

import { faTimesCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import PopoutSelect, { OptionComponent, OptionProps, PopoutOption } from "../../../../components/PopoutSelect";
// components
import Text from "../../../../components/Text";
import Tooltip from "../../../../components/Tooltip";

// api
import { useCohorts } from "../../../../api/hooks";

// schema
import { ICohortModel } from "../../../../api/services";

// utils
import { getColor } from "../../../../utils/getColor";
import { Hex, RGB } from "../../../../utils/getContrast";

// styles
import css from "./CohortSelect.module.scss";

// constants
const MIN_CHARS_TO_DISPLAY_TITLE = 20;
const MIN_OPTIONS_TO_SHOW_SEARCH = 20;
const CHIP_OPACITY_HEX = "4d"; //changes opacity to 30%

/**
 * Custom option component for CohortSelect
 */
const CohortOptions = ({ children, innerRef, innerProps, data: { value, label } }: PropsWithChildren<OptionProps>) => {
	const { data: cohortMetaData } = useCohorts();
	const cohort = cohortMetaData?.cohorts.find((cohort) => cohort._id === value) || ({} as ICohortModel);

	// color and text for the cohort option
	const optionArea = (
		<div className={css.optionArea}>
			<div style={{ backgroundColor: cohort.color || getColor("neutral", 2) }} className={css.cohortColor} />
			<Text className={css.text} size="caption" weight="regular">
				{children}
			</Text>
		</div>
	);

	return (
		<div ref={innerRef} {...innerProps} key={value} className={css.cohortOption}>
			{
				// If the label is too long, we want to show the full label in the tooltip
				label.length > MIN_CHARS_TO_DISPLAY_TITLE + 3 ? (
					<Tooltip placement="right" message={label} revealed strategy="fixed">
						{optionArea}
					</Tooltip>
				) : (
					optionArea
				)
			}
		</div>
	);
};

export interface ILegendItem {
	id: string | null;
	label: string;
	color: Hex | RGB;
	backgroundColor: Hex | RGB;
}
interface CohortSelectProps {
	selectedCohorts: ICohortModel[];
	legendItems: ILegendItem[];
	maxCohorts?: number;
	cohort: string | null;
	handleCohortSelect: (cohort: string) => void;
	handleCohortRemove: (cohort: string) => void;
	handleAddCohort: (cohort: string) => void;
}

/**
 * Cohort Select component for Engagement pages
 * allows selecting cohorts to filter by, and
 */
const CohortSelect = (props: CohortSelectProps) => {
	const {
		selectedCohorts,
		handleAddCohort,
		legendItems,
		maxCohorts,
		handleCohortRemove,
		handleCohortSelect,
		cohort,
	} = props;
	const { data } = useCohorts();
	const timeoutRef = useRef(null);

	useEffect(() => {
		// clean up timeout on unmount
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	const popoutOptions: PopoutOption[] = useMemo(() => {
		const options =
			data?.cohorts
				// filter out cohorts that are already selected
				.filter((coho) => !selectedCohorts.find((co) => co._id === coho._id))
				// map to PopoutOption
				.map((cohort) => {
					return {
						label: cohort.name,
						value: cohort._id,
					};
				}) || [];
		return options;
	}, [data?.cohorts, selectedCohorts]);

	// added a default to showSearch = false here
	let showSearch = false;
	if (data?.cohorts?.length) {
		showSearch =
			data?.cohorts.length >= MIN_OPTIONS_TO_SHOW_SEARCH &&
			(maxCohorts ? selectedCohorts.length < maxCohorts : true);
	}

	return (
		<div className={css.legend}>
			<PopoutSelect
				options={popoutOptions}
				showSearch={showSearch}
				optionComponent={CohortOptions as OptionComponent}
				onChange={handleAddCohort}
				closeMenuOnSelect={Boolean(maxCohorts) && selectedCohorts.length === maxCohorts}
			/>
			{legendItems.map((item) => {
				const chip = (
					<div
						key={item.id}
						style={{
							color: item.color as string,
							backgroundColor:
								!cohort || cohort === item.id
									? (item.backgroundColor as Hex)
									: (`${item.backgroundColor}${CHIP_OPACITY_HEX}` as Hex),
						}}
						onClick={() => item?.id && handleCohortSelect(item.id)}
						className={`${css.legendLabel} ${css.clickable}`}
					>
						<Text className={`${css.legendText}`} element="span" size="extra-small" weight="semi-bold">
							<span>{item.label}</span>
						</Text>
						{item?.id !== "all" ? (
							<FontAwesomeIcon
								onClick={(e) => {
									e.stopPropagation();
									if (item?.id) {
										handleCohortRemove(item?.id);
									}
								}}
								size="xs"
								className={css.removeIcon}
								icon={faTimesCircle}
							/>
						) : null}
					</div>
				);
				return item.label.length > MIN_CHARS_TO_DISPLAY_TITLE ? (
					<Tooltip placement="bottom" revealed message={item.label} key={item.id}>
						{chip}
					</Tooltip>
				) : (
					chip
				);
			})}
		</div>
	);
};

export default CohortSelect;
